import { useMemo, useState } from "react"
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import type {
  NameType,
  Payload,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent"
import type {
  BudgetUpdateReportLine,
  BudgetUpdateReportMetric,
} from "~/ui-rtk/api/budgetUpdateReportsApi"
import { ExportButton } from "~/ui-rtk/components/ui/controls"
import useDate from "~/ui-rtk/hooks/date"
import {
  exportTableToCSV,
  type SerializableColumnConfig,
} from "~/ui-rtk/utils/csv-export"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

/**
 * Six categorical slots, validated with the dataviz palette checker against
 * the app's dark chart surface (`basic.dark-blue` #101028): lightness band,
 * chroma, CVD separation, normal-vision floor and 3:1 contrast all pass.
 * Used whenever the chart holds six entities or fewer.
 */
export const SERIES_COLORS = [
  "#3987e5",
  "#d95926",
  "#199e70",
  "#c98500",
  "#d55181",
  "#9085e9",
] as const

/**
 * Past six entities no palette keeps every pair distinguishable — the chart
 * then spreads hues by the golden angle (neighbours in the list land far
 * apart on the wheel) and rotates three lightness steps so same-hue lines
 * still separate, and relies on hover emphasis + the tooltip for identity.
 * Colors are assigned by an entity's position in the FULL series list, so a
 * line keeps its color whatever else is toggled on or off.
 */
export function seriesColor(index: number, total: number): string {
  if (total <= SERIES_COLORS.length) return SERIES_COLORS[index]
  const hue = Math.round((index * 137.508) % 360)
  const lightness = [62, 50, 70][index % 3]
  return `hsl(${hue} 72% ${lightness}%)`
}

/** Chart chrome from the theme: surface, one-step-off gridlines, muted ink. */
const SURFACE = "#101028"
const GRID = "#222446"
const MUTED = "#677489"
const INK = "#F8FAFC"

/** Chart height — tall enough that six step lines and their deltas stay legible. */
const CHART_HEIGHT = 480
/** Delta labels sit on every change dot while the plot stays this sparse. */
const MAX_LABELLED_CHANGES = 40

/** Metrics with a numeric value to plot (Status flips have none). */
const CHART_METRICS: BudgetUpdateReportMetric[] = [
  "Daily budget",
  "Lifetime budget",
  "tROAS",
  "tCPA",
  "CPC ceiling",
]

const isRatio = (metric: BudgetUpdateReportMetric) => metric === "tROAS"

/** Money metrics are cents; tROAS is Google's fraction (4.5 → 450%). */
export const formatValue = (
  metric: BudgetUpdateReportMetric,
  value: number | null,
  compact = false,
): string => {
  if (value === null) return "—"
  if (isRatio(metric)) return `${Math.round(value * 100)}%`
  return `$${(value / 100).toLocaleString("en-US", {
    minimumFractionDigits: compact ? 0 : 2,
    maximumFractionDigits: compact ? 0 : 2,
  })}`
}

export interface HistorySeries {
  /** `platform|entity|entityId` — stable across renders and filters. */
  key: string
  label: string
  sublabel: string
  changes: number
}

/** One recorded change of one series, kept on the row it happened at. */
export interface HistoryChange {
  previousValue: number | null
  newValue: number | null
  deltaPercent: number | null
  /** "up/down $Δ (±N%)" from the ledger — both moves, never one alone. */
  deltaLabel: string
  changedBy: string
  source: string
}

/** One x position: the value of every series as of that time (step semantics). */
export interface HistoryRow {
  t: number
  values: Record<string, number | null>
  /** Series that actually changed AT this time, with their previous → new. */
  changes: Record<string, HistoryChange>
}

const seriesKey = (line: BudgetUpdateReportLine) =>
  `${line.platform}|${line.entity}|${line.entityId}`

/**
 * Turn ledger lines of one metric into step-chart rows: the x positions are
 * the range edges plus every change time; each series carries its value
 * forward from its previous change, starting from the first change's
 * `previousValue` at the range start (null when the ledger never knew it, so
 * the line begins at the first recorded change). Failed changes are skipped —
 * they never took effect on the platform.
 */
export function buildHistoryRows(
  lines: BudgetUpdateReportLine[],
  metric: BudgetUpdateReportMetric,
  range: { from: Date; to: Date },
): { series: HistorySeries[]; rows: HistoryRow[] } {
  const byKey = new Map<string, BudgetUpdateReportLine[]>()
  for (const line of lines) {
    // Rows a recommender batch left as is are in the ledger for the record;
    // they moved nothing, so they are no step in the chart either.
    if (
      line.metric !== metric ||
      !line.success ||
      line.newValue === null ||
      line.action === "skip"
    ) {
      continue
    }
    const key = seriesKey(line)
    byKey.set(key, [...(byKey.get(key) ?? []), line])
  }

  const series: HistorySeries[] = [...byKey.entries()]
    .map(([key, changes]) => {
      const last = changes[0]
      return {
        key,
        label: last.entityName || last.entityId,
        sublabel: [last.platform, last.entity, last.campaignName]
          .filter(Boolean)
          .join(" · "),
        changes: changes.length,
      }
    })
    .sort((a, b) => b.changes - a.changes || a.label.localeCompare(b.label))

  const from = range.from.getTime()
  const to = range.to.getTime()
  const times = new Set<number>([from, to])
  for (const changes of byKey.values()) {
    for (const line of changes) times.add(new Date(line.changedAt).getTime())
  }
  const sortedTimes = [...times].sort((a, b) => a - b)

  const rows: HistoryRow[] = sortedTimes.map(t => ({
    t,
    values: {},
    changes: {},
  }))
  for (const [key, changes] of byKey) {
    const ordered = [...changes].sort((a, b) =>
      a.changedAt.localeCompare(b.changedAt),
    )
    let current: number | null = ordered[0].previousValue
    let cursor = 0
    for (const row of rows) {
      while (
        cursor < ordered.length &&
        new Date(ordered[cursor].changedAt).getTime() <= row.t
      ) {
        const line = ordered[cursor]
        current = line.newValue
        if (new Date(line.changedAt).getTime() === row.t) {
          row.changes[key] = {
            previousValue: line.previousValue,
            newValue: line.newValue,
            deltaPercent: line.deltaPercent,
            deltaLabel: line.deltaLabel,
            changedBy: line.changedBy,
            source: line.source,
          }
        }
        cursor += 1
      }
      row.values[key] = current
    }
  }
  return { series, rows }
}

/** Money as dollars, tROAS as percent — the units the chart axis shows. */
const csvNumber = (
  metric: BudgetUpdateReportMetric,
  value: number | null,
): number | "" => {
  if (value === null) return ""
  return isRatio(metric) ? Math.round(value * 1000) / 10 : value / 100
}

/**
 * The plotted matrix as CSV rows: one row per point in time, a column per
 * shown entity with the value in force, and a "Changes" column spelling out
 * every previous → new that happened at that moment.
 */
export function buildChartCsv(
  rows: HistoryRow[],
  shown: HistorySeries[],
  metric: BudgetUpdateReportMetric,
  formatDate: (t: number) => string,
): { columns: SerializableColumnConfig[]; data: Record<string, unknown>[] } {
  const unit = isRatio(metric) ? "%" : "$"
  const columns: SerializableColumnConfig[] = [
    { key: "date", header: "Date", type: "string" },
    ...shown.map(s => ({
      key: s.key,
      header: `${s.label} (${unit})`,
      type: "number",
    })),
    { key: "changes", header: "Changes", type: "string" },
  ]
  const data = rows.map(row => {
    const item: Record<string, unknown> = { date: formatDate(row.t) }
    const changes: string[] = []
    for (const s of shown) {
      item[s.key] = csvNumber(metric, row.values[s.key] ?? null)
      const change = row.changes[s.key]
      if (change) {
        changes.push(
          `${s.label}: ${formatValue(metric, change.previousValue)} → ${formatValue(metric, change.newValue)}` +
            `${change.deltaLabel ? `, ${change.deltaLabel}` : ""}` +
            ` by ${change.changedBy} via ${change.source}`,
        )
      }
    }
    item.changes = changes.join(" | ")
    return item
  })
  return { columns, data }
}

interface Selection {
  metric: BudgetUpdateReportMetric
  keys: string[]
}

/** Every entity on by default — the user asked to see all campaigns at once. */
const defaultSelection = (
  metric: BudgetUpdateReportMetric,
  series: HistorySeries[],
): Selection => ({ metric, keys: series.map(s => s.key) })

/** Tooltip rows shown before folding the rest into "+N more". */
const MAX_TOOLTIP_ROWS = 12

function HistoryTooltip({
  active,
  payload,
  label,
  metric,
  formatDate,
}: {
  active?: boolean
  payload?: Payload<ValueType, NameType>[]
  label?: number
  metric: BudgetUpdateReportMetric
  formatDate: (t: number) => string
}) {
  if (!active || !payload?.length || label === undefined) return null
  const row = payload[0]?.payload as HistoryRow | undefined
  const rows = payload
    .flatMap(p =>
      typeof p.value === "number" && p.name !== undefined && p.name !== ""
        ? [
            {
              name: String(p.name),
              value: p.value,
              color: p.color,
              dataKey: p.dataKey,
            },
          ]
        : [],
    )
    .map(p => {
      // Recharts hands back the function dataKey; the series key rides on it.
      const key = (p.dataKey as { seriesKey?: string } | undefined)?.seriesKey
      return { ...p, change: key ? row?.changes[key] : undefined }
    })
    .sort(
      (a, b) => (a.change ? -1 : 0) - (b.change ? -1 : 0) || b.value - a.value,
    )
  if (rows.length === 0) return null
  const visible = rows.slice(0, MAX_TOOLTIP_ROWS)
  const hidden = rows.length - visible.length
  return (
    <div className="rounded-lg border border-basic-blue bg-basic-dark-blue/95 px-3 py-2 text-left shadow-xl">
      <div className="mb-1 text-[11px] text-system-gray-400">
        {formatDate(label)}
      </div>
      <table>
        <tbody>
          {visible.map(entry => (
            <tr key={entry.name} className="align-top">
              <td className="pr-2 pt-1">
                <span
                  className="inline-block h-0.5 w-3 rounded"
                  style={{ backgroundColor: entry.color }}
                />
              </td>
              <td className="max-w-[14rem] truncate pr-3 text-[12px] text-system-gray-300">
                {entry.name}
              </td>
              <td className="text-right text-[12px] tabular-nums">
                {entry.change ? (
                  <div className="flex flex-col items-end">
                    <span className="font-semibold text-white">
                      {formatValue(metric, entry.change.previousValue)} →{" "}
                      {formatValue(metric, entry.change.newValue)}
                      {entry.change.deltaLabel && (
                        <span
                          className={cn(
                            "ml-1",
                            entry.change.deltaLabel.startsWith("-")
                              ? "text-rose-300"
                              : "text-emerald-300",
                          )}
                        >
                          {entry.change.deltaLabel}
                        </span>
                      )}
                    </span>
                    <span className="text-[11px] text-system-gray-500">
                      {entry.change.source} · {entry.change.changedBy}
                    </span>
                  </div>
                ) : (
                  <span className="text-system-gray-300">
                    {formatValue(metric, entry.value)}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {hidden > 0 && (
        <div className="mt-1 text-[11px] text-system-gray-500">
          +{hidden} more — hover a legend chip to single one out
        </div>
      )}
    </div>
  )
}

interface HistoryChartProps {
  lines: BudgetUpdateReportLine[]
  range: { from: Date; to: Date }
}

/**
 * Step chart of how each campaign / ad set's budget (or tROAS) moved through
 * the range — one line per entity, a marker on every recorded change with its
 * delta, the value carried flat in between, and the tooltip reading
 * "previous → new" at a change. Every entity is shown by default; the
 * legend chips below double as the selector, and hovering a chip or a line
 * emphasises that entity and fades the rest.
 */
export default function HistoryChart({ lines, range }: HistoryChartProps) {
  const dayjs = useDate()

  const availableMetrics = useMemo(
    () =>
      CHART_METRICS.filter(metric =>
        lines.some(l => l.metric === metric && l.success),
      ),
    [lines],
  )
  const [pickedMetric, setPickedMetric] =
    useState<BudgetUpdateReportMetric | null>(null)
  const metric =
    pickedMetric && availableMetrics.includes(pickedMetric)
      ? pickedMetric
      : (availableMetrics[0] ?? "Daily budget")

  const { series, rows } = useMemo(
    () => buildHistoryRows(lines, metric, range),
    [lines, metric, range],
  )

  const [selection, setSelection] = useState<Selection | null>(null)
  const active =
    selection && selection.metric === metric
      ? selection
      : defaultSelection(metric, series)
  const [focused, setFocused] = useState<string | null>(null)

  const toggle = (key: string) =>
    setSelection({
      metric,
      keys: active.keys.includes(key)
        ? active.keys.filter(k => k !== key)
        : [...active.keys, key],
    })
  const selectAll = () => setSelection({ metric, keys: series.map(s => s.key) })
  const selectNone = () => setSelection({ metric, keys: [] })

  const shown = series.filter(s => active.keys.includes(s.key))
  const indexOf = new Map(series.map((s, index) => [s.key, index]))
  const colorOf = (key: string) =>
    seriesColor(indexOf.get(key) ?? 0, series.length)
  const isFaded = (key: string) => focused !== null && focused !== key
  const changeCount = rows.reduce(
    (sum, row) => sum + shown.filter(s => row.changes[s.key]).length,
    0,
  )
  const labelDeltas = changeCount <= MAX_LABELLED_CHANGES
  const directLabels = shown.length >= 2 && shown.length <= 4
  const lastIndex = rows.length - 1
  const formatDate = (t: number) => dayjs(t).format("MMM D, YYYY HH:mm")

  const handleExport = () => {
    const { columns, data } = buildChartCsv(rows, shown, metric, t =>
      dayjs(t).format("YYYY-MM-DD HH:mm"),
    )
    const stamp = `${dayjs(range.from).format("YYYY-MM-DD")}_${dayjs(range.to).format("YYYY-MM-DD")}`
    const slug = metric.toLowerCase().replace(/[^a-z0-9]+/g, "-")
    exportTableToCSV(data, columns, {
      filename: `budget-history-chart-${slug}-${stamp}.csv`,
    })
  }

  // A function dataKey per series; the series key is pinned on the function
  // so the tooltip can find that series' change on the hovered row.
  const accessors = useMemo(() => {
    const map: Record<
      string,
      ((row: HistoryRow) => number | null) & { seriesKey: string }
    > = {}
    for (const s of series) {
      const fn = ((row: HistoryRow) => row.values[s.key]) as ((
        row: HistoryRow,
      ) => number | null) & { seriesKey: string }
      fn.seriesKey = s.key
      map[s.key] = fn
    }
    return map
  }, [series])

  if (availableMetrics.length === 0) {
    return (
      <div
        className="flex items-center justify-center rounded-lg border border-basic-blue text-sm text-system-gray-500"
        style={{ height: CHART_HEIGHT }}
      >
        No applied budget or tROAS changes to chart in this range.
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Metric selector — one axis per chart, so one metric at a time. */}
      <div className="flex items-center gap-1">
        {availableMetrics.map(m => (
          <button
            key={m}
            type="button"
            onClick={() => setPickedMetric(m)}
            className={cn(
              "rounded-md px-2 py-1 text-[12px] transition-colors",
              m === metric
                ? "bg-system-blue-600 font-semibold text-white"
                : "bg-white/5 text-system-gray-400 hover:bg-white/10 hover:text-system-gray-200",
            )}
          >
            {m}
          </button>
        ))}
        <span className="ml-auto text-[11px] text-system-gray-500">
          Each marker is a recorded change (previous → new); failed changes are
          not plotted.
        </span>
        <ExportButton onExport={handleExport} disabled={shown.length === 0}>
          Export table
        </ExportButton>
      </div>

      <div
        className="rounded-lg border border-basic-blue p-2"
        style={{ backgroundColor: SURFACE, height: CHART_HEIGHT }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={rows}
            margin={{
              top: 20,
              right: directLabels ? 130 : 24,
              bottom: 4,
              left: 4,
            }}
          >
            <CartesianGrid stroke={GRID} strokeWidth={1} vertical={false} />
            <XAxis
              dataKey="t"
              type="number"
              scale="time"
              domain={[range.from.getTime(), range.to.getTime()]}
              tickFormatter={t => dayjs(t).format("MMM D")}
              tick={{ fill: MUTED, fontSize: 11 }}
              axisLine={{ stroke: GRID }}
              tickLine={false}
              minTickGap={32}
            />
            <YAxis
              domain={[0, "auto"]}
              tickFormatter={v => formatValue(metric, Number(v), true)}
              tick={{ fill: MUTED, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={72}
            />
            <Tooltip
              cursor={{ stroke: MUTED, strokeWidth: 1 }}
              animationDuration={0}
              content={props => (
                <HistoryTooltip
                  active={props.active}
                  payload={props.payload}
                  label={
                    typeof props.label === "number" ? props.label : undefined
                  }
                  metric={metric}
                  formatDate={formatDate}
                />
              )}
            />
            {shown.map(s => {
              const color = colorOf(s.key)
              return (
                <Line
                  key={s.key}
                  type="stepAfter"
                  name={s.label}
                  dataKey={accessors[s.key]}
                  stroke={color}
                  strokeWidth={focused === s.key ? 3 : 2}
                  strokeOpacity={isFaded(s.key) ? 0.15 : 1}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  isAnimationActive={false}
                  connectNulls={false}
                  onMouseEnter={() => setFocused(s.key)}
                  onMouseLeave={() => setFocused(null)}
                  activeDot={{
                    r: 6,
                    stroke: SURFACE,
                    strokeWidth: 2,
                    fill: color,
                  }}
                  dot={props => {
                    const { cx, cy, index, payload } = props as {
                      cx?: number
                      cy?: number
                      index: number
                      payload: HistoryRow
                    }
                    if (cx === undefined || cy === undefined) {
                      return <g key={`${s.key}-${index}`} />
                    }
                    const change = payload.changes[s.key]
                    const isEnd = directLabels && index === lastIndex
                    return (
                      <g
                        key={`${s.key}-${index}`}
                        opacity={isFaded(s.key) ? 0.15 : 1}
                      >
                        {change && (
                          <>
                            <circle
                              cx={cx}
                              cy={cy}
                              r={5}
                              fill={color}
                              stroke={SURFACE}
                              strokeWidth={2}
                            />
                            {labelDeltas && change.deltaLabel && (
                              <text
                                x={cx}
                                y={cy - 10}
                                fill={INK}
                                fontSize={11}
                                fontWeight={600}
                                textAnchor="middle"
                              >
                                {change.deltaLabel}
                              </text>
                            )}
                          </>
                        )}
                        {isEnd && payload.values[s.key] !== null && (
                          <text x={cx + 8} y={cy + 4} fill={INK} fontSize={11}>
                            {s.label.length > 18
                              ? `${s.label.slice(0, 17)}…`
                              : s.label}
                          </text>
                        )}
                      </g>
                    )
                  }}
                />
              )
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Legend + selector: every entity with a change. Hover = emphasise. */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-[11px] text-system-gray-500">
          {shown.length}/{series.length} shown
        </span>
        <button
          type="button"
          onClick={selectAll}
          className="rounded-md bg-white/5 px-2 py-1 text-[12px] text-system-gray-400 hover:bg-white/10 hover:text-system-gray-200"
        >
          All
        </button>
        <button
          type="button"
          onClick={selectNone}
          className="mr-2 rounded-md bg-white/5 px-2 py-1 text-[12px] text-system-gray-400 hover:bg-white/10 hover:text-system-gray-200"
        >
          None
        </button>
        {series.map(s => {
          const isOn = active.keys.includes(s.key)
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => toggle(s.key)}
              onMouseEnter={() => isOn && setFocused(s.key)}
              onMouseLeave={() => setFocused(null)}
              title={s.sublabel}
              className={cn(
                "flex items-center gap-1.5 rounded-md border px-2 py-1 text-[12px] transition-colors",
                isOn
                  ? "border-white/20 bg-white/10 text-white"
                  : "border-transparent bg-white/5 text-system-gray-400 hover:bg-white/10 hover:text-system-gray-200",
                focused === s.key && "border-white/60",
              )}
            >
              <span
                className="inline-block h-0.5 w-3 rounded"
                style={{
                  backgroundColor: isOn ? colorOf(s.key) : MUTED,
                }}
              />
              <span className="max-w-[12rem] truncate">{s.label}</span>
              <span className="rounded bg-white/10 px-1 text-[10px] tabular-nums text-system-gray-400">
                {s.changes}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
